/**
 * The auth task's `SessionState`, carried in a context this module owns.
 *
 * `useSession()` is the frozen contract and reaching for it directly from
 * `useMe()` would make every test of the shell mount an `AuthProvider`, a real
 * `loadConfig()` and a real Firebase app in order to say anything about a
 * capability. One thin adapter (`./AuthBridge`) reads the contract once, at the
 * top of the tree, and everything below reads it from here -- so the shell
 * depends on the *shape* of the session rather than on the provider that
 * produces it, and a test can hold the session in a state (`'loading'`) that
 * Firebase only occupies for a few hundred milliseconds.
 *
 * That window is not cosmetic. Firebase answers `onAuthStateChanged` after the
 * first render, so a returning user with a valid persisted session is briefly
 * indistinguishable from a signed-out one. Anything that asks the API who the
 * caller is before that answer arrives asks without a token and is refused.
 */

import { createContext, useContext } from 'react';

import type { SessionState } from '../auth/useSession';

export type { SessionState };

export const AuthStateContext = createContext<SessionState | null>(null);

/**
 * Throws rather than defaulting to `'signed-out'`. A default here would be the
 * same bug the context exists to prevent, one layer down: every consumer would
 * silently behave as though the answer had arrived and been negative.
 */
export function useAuthState(): SessionState {
  const state = useContext(AuthStateContext);
  if (state === null) {
    throw new Error('no session in context: wrap the tree in <AppProviders>');
  }
  return state;
}

/** The one action alongside the read-only state above: ending the session.
 *  A second context, not a field on `SessionState` -- that type is read-only
 *  data, and a test holding a bare `SessionState` has no action to put there. */
export const SignOutContext = createContext<(() => Promise<void>) | null>(null);

export function useSignOut(): () => Promise<void> {
  const signOut = useContext(SignOutContext);
  if (signOut === null) {
    throw new Error('no sign-out action in context: wrap the tree in <AppProviders>');
  }
  return signOut;
}
